// "Forecasts you may be interested in" on the new-question page: which of
// Sayer's own forecasts (the snapshot proto/pull-featured.sh writes) to show today, and how a
// card reads them. Pure (no React).

import { answerSummary, pctText, shortDate } from "../forecast/answerSummary.js";
import { bucketLabel } from "../forecast/multiway.js";

export const FEATURED_COUNT = 8;
/**
 * The first row of cards: drawn from the domains the user forecasts in, and the row the search
 * fills with matches as they type (hooks/useFeaturedSearch.js).
 */
export const TOP_ROW = 4;
/** The user's domains: their most-used ones, until they cover this share of their forecasts. */
const INTEREST_COVERAGE = 0.6;
const MAX_INTEREST_DOMAINS = 5;

/** The local calendar day, "2026-10-15": the cards reshuffle once a day. */
export function dayKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A small seeded generator (mulberry32 over a string hash), so a day always draws the same. */
export function seededRandom(seed) {
  let h = 1779033703 ^ String(seed).length;
  for (const ch of String(seed)) {
    h = Math.imul(h ^ ch.charCodeAt(0), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffled(list, rand) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * A Sayer question key (`Polymarket#f_7263521025`) as a telemetry key (`data-ui-key`, surface
 * key): those must be id-shaped (`[A-Za-z0-9._:-]{1,64}`, services/interactionCapture.js), so
 * the `#` becomes `:` and any other character outside that set `_`. Null for no key.
 */
export function questionUiKey(questionKey) {
  if (typeof questionKey !== "string" || !questionKey) return null;
  return questionKey.replace(/#/g, ":").replace(/[^A-Za-z0-9._:-]/g, "_");
}

export const domainOf = (item) => item?.tags?.domain || "Other";

/**
 * The domains a user forecasts in: their most-used domains, largest first, until those cover
 * 60% of their tagged forecasts (at most five). Someone who forecasts across everything still
 * gets a short list.
 * @param {Record<string, number>|null|undefined} counts domain -> number of their forecasts
 * @returns {string[]}
 */
export function interestDomains(counts) {
  const entries = Object.entries(counts || {})
    .filter(([d, n]) => d && d !== "Other" && Number.isFinite(n) && n > 0)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  const total = entries.reduce((s, [, n]) => s + n, 0);
  const out = [];
  let covered = 0;
  for (const [d, n] of entries) {
    if (
      out.length >= MAX_INTEREST_DOMAINS ||
      (out.length > 0 && covered / total >= INTEREST_COVERAGE)
    )
      break;
    out.push(d);
    covered += n;
  }
  return out;
}

/** Items with a market price first and the rest after, alternating, so a domain's draw mixes both. */
function mixMarkets(list) {
  const priced = list.filter((i) => typeof i.marketP === "number");
  const rest = list.filter((i) => typeof i.marketP !== "number");
  const out = [];
  for (let i = 0; out.length < list.length; i++) {
    if (i < priced.length) out.push(priced[i]);
    if (i < rest.length) out.push(rest[i]);
  }
  return out;
}

/** One item per domain in turn, in `domains` order, until `n` are taken. */
function roundRobin(byDomain, domains, n, taken) {
  const out = [];
  let progress = true;
  while (out.length < n && progress) {
    progress = false;
    for (const d of domains) {
      if (out.length >= n) break;
      const queue = byDomain.get(d);
      while (queue && queue.length && taken.has(queue[0].questionKey)) queue.shift();
      if (queue && queue.length) {
        const it = queue.shift();
        taken.add(it.questionKey);
        out.push(it);
        progress = true;
      }
    }
  }
  return out;
}

/**
 * `n` items from `domains`, each one's domain drawn in proportion to `weights` (a domain with no
 * items left drops out), so a domain the user asks about five times as often fills about five
 * times as many cards.
 */
function weightedDraw(byDomain, domains, weights, n, taken, rand) {
  const out = [];
  while (out.length < n) {
    const live = domains.filter((d) => {
      const queue = byDomain.get(d);
      while (queue && queue.length && taken.has(queue[0].questionKey)) queue.shift();
      return queue && queue.length;
    });
    if (!live.length) break;
    const weight = (d) => Math.max(1, weights[d] || 1);
    let r = rand() * live.reduce((sum, d) => sum + weight(d), 0);
    const d = live.find((x) => (r -= weight(x)) < 0) ?? live[live.length - 1];
    const it = byDomain.get(d).shift();
    taken.add(it.questionKey);
    out.push(it);
  }
  return out;
}

/**
 * The cards, in page order. The top row (the first TOP_ROW) comes from the user's own domains,
 * each card's domain drawn by how often they use it; the rest, and the top row of a user with no tagged
 * forecasts, are a spread across all domains. Each row is shuffled within itself. The same
 * `seed` always draws the same cards; without one, the date is the seed. Questions in `exclude`
 * (ones the user already has) are never drawn; to keep a page's cards in place as the user adds
 * some of them, see refillSlots.
 *
 * @param {Array<object>} items the snapshot's items
 * @param {{userDomains?: Record<string, number>, seed?: string, date?: Date, count?: number,
 *   exclude?: Iterable<string>}} [opts]
 * @returns {Array<object & {reason: "personal"|"spread"}>}
 */
export function pickFeatured(
  items,
  { userDomains = {}, seed = null, date = new Date(), count = FEATURED_COUNT, exclude = [] } = {},
) {
  const list = (Array.isArray(items) ? items : []).filter((i) => i && i.questionKey && i.title);
  if (!list.length) return [];
  const rand = seededRandom(seed ?? dayKey(date));
  const byDomain = new Map();
  for (const it of shuffled(list, rand)) {
    const d = domainOf(it);
    if (!byDomain.has(d)) byDomain.set(d, []);
    byDomain.get(d).push(it);
  }
  for (const [d, q] of byDomain) byDomain.set(d, mixMarkets(q));
  const allDomains = shuffled([...byDomain.keys()].sort(), rand);
  const counts = userDomains || {};
  const mine = interestDomains(counts).filter((d) => byDomain.has(d));
  const taken = new Set(exclude);
  const topCount = Math.min(TOP_ROW, count);
  const personal = weightedDraw(byDomain, mine, counts, topCount, taken, rand).map((it) => ({
    ...it,
    reason: "personal",
  }));
  // One draw for every other card, so the spread runs across domains from the top row's last
  // card to the bottom row's.
  const spread = roundRobin(byDomain, allDomains, count - personal.length, taken).map((it) => ({
    ...it,
    reason: "spread",
  }));
  const top = [...personal, ...spread.slice(0, topCount - personal.length)];
  const bottom = spread.slice(topCount - personal.length);
  return [...shuffled(top, rand), ...shuffled(bottom, rand)];
}

/**
 * The page's cards as the user adds some of them to their forecasts. `slots` holds, for each
 * slot, the cards that have taken it in turn; a slot shows the first of them the user does not
 * have, so every other slot keeps its card, and a card whose copy is undone takes its slot back
 * (the cards that took the slot after it are let go). A slot whose cards the user all has takes
 * a card from `draw` called without the user's cards and the ones the slots hold, from the same
 * row when it has one, so a top-row slot is refilled from the user's domains while they have
 * cards left; a slot nothing is left to take is dropped. With no slots yet, `draw` fills them.
 *
 * @param {object[][]|null} slots each slot's cards, in the order they took it
 * @param {(exclude: string[]) => object[]} draw pickFeatured for the page, without `exclude`
 * @param {Iterable<string>} exclude question keys the user now has
 * @returns {{slots: object[][], cards: object[]}} the slots, and the cards to show in page order
 */
export function refillSlots(slots, draw, exclude) {
  const owned = new Set(exclude);
  if (!Array.isArray(slots) || !slots.length) {
    const drawn = draw([...owned]);
    return { slots: drawn.map((card) => [card]), cards: drawn };
  }
  // Each slot up to the card it shows: a later card only took it while that one was the user's.
  const next = slots.map((chain) => {
    const at = chain.findIndex((c) => !owned.has(c.questionKey));
    return at < 0 ? chain.slice() : chain.slice(0, at + 1);
  });
  const held = new Set(next.flat().map((c) => c.questionKey));
  let drawn = null;
  const spare = (from) => {
    const it = from.find((c) => !held.has(c.questionKey));
    if (it) held.add(it.questionKey);
    return it;
  };
  const cards = [];
  next.forEach((chain, i) => {
    let card = chain.find((c) => !owned.has(c.questionKey));
    if (!card) {
      drawn ??= draw([...new Set([...owned, ...held])]);
      const top = drawn.slice(0, TOP_ROW);
      card =
        (i < TOP_ROW
          ? spare(top.filter((c) => c.reason === "personal")) || spare(top)
          : spare(drawn.slice(TOP_ROW))) || spare(drawn);
      if (card) chain.push(card);
    }
    if (card) cards.push(card);
  });
  return { slots: next, cards };
}

/** The contract-shaped source answerSummary reads, built from a snapshot item. */
export function itemSource(item) {
  const fv = item?.forecastValue ?? null;
  const mass = fv?.representation?.mass;
  if (item?.kind === "binary") {
    const p = Array.isArray(mass) && mass.length === 2 ? mass[1] : (item?.headline?.p ?? null);
    return { contract: { probability: p, forecast_value: fv, outcome_labels: ["no", "yes"] } };
  }
  return { contract: { probability: null, forecast_value: fv } };
}

/** A snapshot item's yes/no answer: the snapshot's own headline when it carries no PMF. */
function binarySummary(item) {
  const a = answerSummary(itemSource(item));
  return a.kind === "binary" || typeof item?.headline?.p !== "number"
    ? a
    : answerSummary({ contract: { probability: item.headline.p } });
}

/**
 * Sayer's answer on a card: the larger side of a yes/no question, else the leading named
 * outcome. An outcome is never called the leader when outcomes not listed hold more.
 * @returns {{kind: "binary"|"multi", value: string, label: string, text: string}|null}
 */
export function cardAnswer(item) {
  const a = item?.kind === "binary" ? binarySummary(item) : answerSummary(itemSource(item));
  if (a.kind === "binary") {
    const { headlineText: value, headlineLabel: label } = a;
    return { kind: "binary", value, label, text: `${value} ${label}` };
  }
  if (a.kind === "multi" && a.leader) {
    const value = pctText(a.leader.p);
    const label = a.otherLeads
      ? "An outcome not listed"
      : a.otherLarger
        ? `${a.leader.label}, leading the named field`
        : a.leader.label;
    return { kind: "multi", value, label, text: `${value} ${label}` };
  }
  const h = item?.headline;
  if (item?.kind !== "binary" && h && typeof h.p === "number") {
    const value = pctText(h.p);
    return { kind: "multi", value, label: h.label, text: `${value} ${h.label}` };
  }
  return null;
}

/**
 * The market's price beside Sayer's answer, or null when there is no market (Sayer's own
 * questions). On a yes/no question it is the price of the side Sayer names (`marketP` is the
 * Yes price); on a multi-outcome question it is the price of the market's favourite, which
 * need not be Sayer's leader.
 * @returns {{label: string, value: string, text: string, title: string}|null}
 */
export function cardMarket(item, { now = new Date() } = {}) {
  const p = item?.marketP;
  if (typeof p !== "number" || !Number.isFinite(p)) return null;
  const when = item.marketAt ? shortDate(item.marketAt, now) : "";
  const title =
    item.marketSource === "report"
      ? `The market's price when Sayer made this forecast${when ? ` (${when})` : ""}`
      : `The market's latest price${when ? ` (${when})` : ""}`;
  if (item.kind === "binary") {
    const noLeads = binarySummary(item).leader?.label === "No";
    const value = pctText(noLeads ? 1 - p : p);
    const label = `chance of ${noLeads ? "No" : "Yes"}`;
    return { label, value, text: `${value} ${label}`, title };
  }
  const value = pctText(p);
  const label = item.marketLabel ? bucketLabel({ label: item.marketLabel }) : "";
  return { label, value, text: label ? `${value} ${label}` : value, title };
}

/** The snapshot's items, or [] for a missing or malformed snapshot. */
export function snapshotItems(snapshot) {
  return Array.isArray(snapshot?.items) ? snapshot.items : [];
}
